import {useCallback, useMemo, useRef} from 'react';
import {addDoc, collection, deleteDoc, deleteField, doc, query, updateDoc,} from 'firebase/firestore';
import {useCollection} from 'react-firebase-hooks/firestore';
import {Timetable, TimetableInsertOptions, TimetableUpdateOptions} from '../types/Timetable';
import {firestore} from "../App";
import moment from "moment/moment";
import {MONGO_DATE_TIME_FORMAT} from "../utils/date";
import isEqual from 'lodash/isEqual';

interface UseTimetableManagerProps {
    plannerId: string;
}

export function useTimetableManager({plannerId}: UseTimetableManagerProps) {

    const timetablesQuery = useMemo(() => {
        if (!plannerId) return null;

        return query(
            collection(firestore, 'syncedPlanners', plannerId, 'timetables')
        );
    }, [plannerId]);

    const [timetablesSnapshot, loadingTimetables, errorTimetables] = useCollection(timetablesQuery);

    // Store previous timetables to compare
    const prevTimetablesRef = useRef<Timetable[]>([]);

    const timetables = useMemo(() => {
        if (!timetablesSnapshot?.docs) return prevTimetablesRef.current;

        const newTimetables = timetablesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Timetable));

        // Only return new array if data actually changed
        if (isEqual(newTimetables, prevTimetablesRef.current)) {
            return prevTimetablesRef.current;
        }

        prevTimetablesRef.current = newTimetables;
        return newTimetables;
    }, [timetablesSnapshot]);

    /**
     * Inserts a timetable document in the database.
     */
    const createTimetable = useCallback(async (options: TimetableInsertOptions) => {
        if (!plannerId) return;

        const timetableDoc: any = {
            plannerId: plannerId,
            ...options, // Spread all options directly
            createdOn: moment().format(MONGO_DATE_TIME_FORMAT)
        };

        // Firestore does not allow 'undefined' values.
        Object.keys(timetableDoc).forEach(key => {
            if (timetableDoc[key] === undefined) {
                delete timetableDoc[key];
            }
        });

        const colRef = collection(firestore, 'syncedPlanners', plannerId, 'timetables');

        try {
            console.log("Creating Timetable: ", timetableDoc);
            const docRef = await addDoc(colRef, timetableDoc);
            console.log("Timetable created successfully with ID: ", docRef.id);
            return docRef.id;
        } catch (error) {
            console.error("Error creating Timetable: ", error);
        }
    }, [plannerId]);

    /**
     * Updates a timetable document in the database.
     */
    const updateTimetable = useCallback(async (timetableId: string, options: TimetableUpdateOptions) => {
        if (!plannerId) return;

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'timetables', timetableId);

        // Build the update object, explicitly handling optional fields
        const updateData = {
            ...options, // Spread all options directly
            startDate: options.startDate ?? deleteField(),
            endDate: options.endDate ?? deleteField(),
        };

        await updateDoc(docRef, updateData);
    }, [plannerId]);

    /**
     * Deletes a timetable from the database.
     * WARNING: This will NOT delete subcollections like lessons.
     */
    const deleteTimetable = useCallback(async (timetableId: string) => {
        if (!plannerId) return;

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'timetables', timetableId);
        await deleteDoc(docRef);
    }, [plannerId]);

    console.log("useTimetableManager: ", timetables);

    return {
        timetables,
        loadingTimetables,
        errorTimetables,
        createTimetable,
        updateTimetable,
        deleteTimetable
    };
}